// Copied from https://github.com/lifeomic/life-extend/blob/master/src/api/phc/phc.ts

import { findHumanName } from '@lifeomic/fhir-constants';
import type { PlatformDefaultBundle } from '@lifeomic/service-clients/fhir-client';
import { MrnConfigType } from '../common/types/Projects';

export const UNITS = 'http://lifeomic.com/fhir/units';
export const LXP = 'lxp';

export const LOINC_SYSTEM = 'http://loinc.org';
export const LOINC_ACTIVITY_SYSTEM = 'http://lifeomic.com/fhir/activity';
export const LOINC_NUTRITION_SYSTEM = 'http://lifeomic.com/fhir/nutrition';
export const SNOMED_SYSTEM = 'http://snomed.info/sct';
export const FHIR_RACE_SYSTEM = 'http://hl7.org/fhir/v3/Race';
export const UNITS_OF_MEASURE_SYSTEM = 'http://unitsofmeasure.org';
export const BIOSENSE_SYSTEM = 'https://mybiosense.com/biosense-research';
export const BIOSENSE_ACE = 'ACE';

export const CODE_SYSTEMS: { [key: string]: fhir.Coding } = {
  height: {
    system: LOINC_SYSTEM,
    code: '8302-2',
    display: 'body height',
  },
  weight: {
    system: LOINC_SYSTEM,
    code: '29463-7',
    display: 'body weight',
  },
  glucose: {
    system: LOINC_SYSTEM,
    code: '15074-8',
    display: 'glucose [Moles/​volume] in blood',
  },
  ketone: {
    system: LOINC_SYSTEM,
    code: '53061-8',
    display: 'ketones [Moles/volume] in blood',
  },
  breathKetone: {
    system: BIOSENSE_SYSTEM,
    code: BIOSENSE_ACE,
    display: 'breath acetone',
  },
  waistCircumference: {
    system: LOINC_SYSTEM,
    code: '8280-0',
    display: 'waist circumference',
  },
  chestCircumference: {
    system: LOINC_SYSTEM,
    code: '8279-2',
    display: 'chest circumference',
  },
  neckCircumference: {
    system: LOINC_SYSTEM,
    code: '56074-8',
    display: 'neck circumference',
  },
  thighCircumferenceLeft: {
    system: LOINC_SYSTEM,
    code: '8285-9',
    display: 'left thigh circumference',
  },
  thighCircumferenceRight: {
    system: LOINC_SYSTEM,
    code: '8286-7',
    display: 'right thigh circumference',
  },
  hipCircumference: {
    system: LOINC_SYSTEM,
    code: '56063-1',
    display: 'hip circumference',
  },
  pef: {
    system: LOINC_SYSTEM,
    code: '33452-4',
    display: 'peak expiratory flow',
  },
  fev: {
    system: LOINC_SYSTEM,
    code: '20150-9',
    display: 'forced expiratory volume',
  },
  heartRate: {
    system: LOINC_SYSTEM,
    code: '8867-4',
    display: 'resting heart rate',
  },
  bloodPressure: {
    system: LOINC_SYSTEM,
    code: '85354-9',
    display: 'blood pressure systolic & diastolic',
  },
  systolicBloodPressure: {
    system: LOINC_SYSTEM,
    code: '8480-6',
    display: 'blood pressure systolic',
  },
  diastolicBloodPressure: {
    system: LOINC_SYSTEM,
    code: '8462-4',
    display: 'blood pressure diastolic',
  },
  bodyFat: {
    system: LOINC_SYSTEM,
    code: '41982-0',
    display: 'body fat percentage',
  },
  gripStrengthLeft: {
    system: LOINC_SYSTEM,
    code: '83173-5',
    display: 'left hand grip strength',
  },
  gripStrengthRight: {
    system: LOINC_SYSTEM,
    code: '83174-3',
    display: 'right hand grip strength',
  },
  suprailiacSkinfold: {
    system: LOINC_SYSTEM,
    code: '8355-0',
    display: 'Suprailiac skinfold',
  },
  food: {
    system: LOINC_NUTRITION_SYSTEM,
    code: '453091d6-3c20-467a-8a41-c555fd62920e',
    display: 'healthy plants',
  },
  sleep: {
    system: SNOMED_SYSTEM,
    code: '258158006',
    display: 'Sleep',
  },
  activity: {
    system: LOINC_ACTIVITY_SYSTEM,
    code: '000',
    display: 'Activity',
  },
  mindful: {
    system: LOINC_ACTIVITY_SYSTEM,
    code: '011',
    display: 'Mindful',
  },
  tobacco: {
    system: 'http://lifeomic.com/fhir/tobacco',
    code: '021db2db-3aa8-4ab6-be99-5265be3c0ad8',
    display: 'Tobacco',
  },
  race: {
    system: 'http://lifeomic.com/fhir/race',
    code: '4bad7758-0175-4a8d-8136-396113e75e79',
    display: 'Race',
  },
  education: {
    system: 'http://lifeomic.com/fhir/education-level',
    code: '4ba5fc26-49f0-4028-8f76-36cbac7d09bf',
    display: 'Education',
  },
  primaryHealthConcern: {
    system: 'http://lifeomic.com/fhir/primary-concern',
    code: 'fac3ca9a-8880-478e-95ec-f39d50e7cddf',
    display: 'Primary Health Concern',
  },
  occupation: {
    system: 'http://lifeomic.com/fhir/occupation-category',
    code: '8eb2a3ff-1e27-4fa5-828e-22fe95c55e43',
    display: 'Occupation',
  },
  diagnosedConditions: {
    system: 'http://lifeomic.com/fhir/diagnosed-conditions',
    code: 'cef562e5-c61e-454c-b87d-106a54048363',
    display: 'Conditions',
  },
  stress: {
    system: 'http://lifeomic.com/fhir/stress-level',
    code: '1078a148-39b7-43cf-abbd-3affe3c65ee1',
    display: 'Stress',
  },
  exercisePattern: {
    system: 'http://lifeomic.com/fhir/exercise-pattern',
    code: 'c04547e6-944a-43bb-bf98-e4ef5c02a774',
    display: 'Exercise Pattern',
  },
  familyConditions: {
    system: 'http://lifeomic.com/fhir/family-conditions',
    code: 'cced3fb9-7e86-4fee-ab1b-3a402cb37291',
    display: 'Family Conditions',
  },
  dietaryPattern: {
    system: 'http://lifeomic.com/fhir/dietary-pattern',
    code: '77963f19-3d46-4014-bc77-3f32f4c24c57',
    display: 'Dietary Pattern',
  },
  wellnessProfessional: {
    system: 'http://lifeomic.com/fhir/wellness-professional',
    code: '49ef3cb3-f08c-452b-80a7-40da5f8043ae',
    display: 'Wellness Professional',
  },
  fast: {
    system: SNOMED_SYSTEM,
    code: '16985007',
    display: 'Fasting',
  },
};

export interface ProcedureCodeDisplay {
  code: string;
  display: string;
}

export interface CodingInput {
  code: string | null;
  display: string | null;
  system: string | null;
}

export enum ObservationType {
  Nutrition = 'Fruit / Vegetable',
  Weight = 'Weight',
  Ketones = 'Ketones',
  BreathKetones = 'Breath Ketones',
  Height = 'Height',
  BloodGlucose = 'Blood Glucose',
  BloodPressure = 'Blood Pressure',
  BloodPressureSystolic = 'Blood Pressure Systolic',
  BloodPressureDiastolic = 'Blood Pressure Diastolic',
  WaistCircumference = 'Waist Circumference',
  ChestCircumference = 'Chest Circumference',
  NeckCircumference = 'Neck Circumference',
  ThighCircumferenceLeft = 'Thigh Left Circumference',
  ThighCircumferenceRight = 'Thigh Right Circumference',
  HipCircumference = 'Hip Circumference',
  HeartRate = 'Heart Rate',
  Pef = 'Peak Expiratory Flow',
  Fev = 'Forced Expiratory Volume',
  Tobacco = 'Tobacco',
  Race = 'Race',
  Education = 'Education',
  Occupation = 'Occupation',
  DiagnosedConditions = 'Diagnosed Conditions',
  FamilyConditions = 'Family Conditions',
  Stress = 'Stress',
  ExercisePattern = 'Excercise Pattern',
  DietaryPattern = 'Dietary',
  PrimaryHealthConcern = 'Primary Health Concern',
  WellnessProfessional = 'Wellness Professional',
  BodyFat = 'Body Fat Percentage',
  GripStrengthLeft = 'Grip Strength Left',
  GripStrengthRight = 'Grip Strength Right',
  SuprailiacSkinfold = 'Suprailiac Skinfold',
}

export const MeasurableObseravationKeys = [
  ObservationType.Nutrition,
  ObservationType.Weight,
  ObservationType.Ketones,
  ObservationType.BreathKetones,
  ObservationType.Height,
  ObservationType.BloodGlucose,
  ObservationType.BloodPressure,
  ObservationType.BloodPressureSystolic,
  ObservationType.BloodPressureDiastolic,
  ObservationType.WaistCircumference,
  ObservationType.ChestCircumference,
  ObservationType.NeckCircumference,
  ObservationType.ThighCircumferenceLeft,
  ObservationType.ThighCircumferenceRight,
  ObservationType.HeartRate,
  ObservationType.Pef,
  ObservationType.Fev,
  ObservationType.Tobacco,
  ObservationType.BodyFat,
  ObservationType.GripStrengthLeft,
  ObservationType.GripStrengthRight,
  ObservationType.HipCircumference,
  ObservationType.SuprailiacSkinfold,
];

export const valueForObservationType = (
  type: ObservationType,
  value: number,
) => {
  switch (type) {
    case ObservationType.Nutrition:
      return {
        value,
        unit: 'arbitrary unit',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: "[arb'U]",
      };
    case ObservationType.Weight:
      return {
        value,
        unit: 'kg',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: 'kg',
      };
    case ObservationType.Ketones:
      return {
        value: value,
        unit: 'mmol/L',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: 'mmol/L',
      };
    case ObservationType.BreathKetones:
      return {
        value: value,
        unit: BIOSENSE_ACE,
        system: BIOSENSE_SYSTEM,
        code: BIOSENSE_ACE,
      };
    case ObservationType.BloodGlucose:
      return {
        value: value,
        unit: 'mmol/L',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: 'mmol/L',
      };
    case ObservationType.Height:
      return {
        value,
        unit: 'cm',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: 'cm',
      };
    case ObservationType.BodyFat:
      return {
        value,
        unit: '%',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: '%',
      };
    case ObservationType.WaistCircumference:
      return {
        value,
        unit: 'cm',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: 'cm',
      };
    case ObservationType.ChestCircumference:
      return {
        value,
        unit: 'cm',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: 'cm',
      };
    case ObservationType.NeckCircumference:
      return {
        value,
        unit: 'cm',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: 'cm',
      };
    case ObservationType.ThighCircumferenceLeft:
      return {
        value,
        unit: 'cm',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: 'cm',
      };
    case ObservationType.ThighCircumferenceRight:
      return {
        value,
        unit: 'cm',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: 'cm',
      };
    case ObservationType.HipCircumference:
      return {
        value,
        unit: 'cm',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: 'cm',
      };
    case ObservationType.SuprailiacSkinfold:
      return {
        value,
        unit: 'cm',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: 'cm',
      };
    case ObservationType.HeartRate:
      return {
        value,
        unit: 'bpm',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: '/min',
      };
    case ObservationType.Pef:
      return {
        value,
        unit: 'L/min',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: '/min',
      };
    case ObservationType.Fev:
      return {
        value,
        unit: 'L',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: 'L',
      };
    case ObservationType.BloodPressure:
      return {
        value,
        unit: 'mmHg',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: 'mm[Hg]',
      };
    case ObservationType.BloodPressureDiastolic: {
      return {
        value,
        unit: 'mmHg',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: 'mm[Hg]',
      };
    }
    case ObservationType.BloodPressureSystolic: {
      return {
        value,
        unit: 'mmHg',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: 'mm[Hg]',
      };
    }
    case ObservationType.GripStrengthLeft: {
      return {
        value,
        unit: 'lb',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: '[lbf_av]',
      };
    }
    case ObservationType.GripStrengthRight: {
      return {
        value,
        unit: 'lb',
        system: UNITS_OF_MEASURE_SYSTEM,
        code: '[lbf_av]',
      };
    }
    // no default
  }
};

export const displayForObservationType = (type: ObservationType): string => {
  switch (type) {
    case ObservationType.Nutrition:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.food.display;
    case ObservationType.Weight:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.weight.display;
    case ObservationType.Ketones:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.ketone.display;
    case ObservationType.BreathKetones:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.breathKetone.display;
    case ObservationType.Height:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.height.display;
    case ObservationType.BloodGlucose:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.glucose.display;
    case ObservationType.WaistCircumference:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.waistCircumference.display;
    case ObservationType.ChestCircumference:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.chestCircumference.display;
    case ObservationType.NeckCircumference:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.neckCircumference.display;
    case ObservationType.ThighCircumferenceLeft:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.thighCircumferenceLeft.display;
    case ObservationType.ThighCircumferenceRight:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.thighCircumferenceRight.display;
    case ObservationType.HipCircumference:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.hipCircumference.display;
    case ObservationType.Pef:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.pef.display;
    case ObservationType.Fev:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.fev.display;
    case ObservationType.HeartRate:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.heartRate.display;
    case ObservationType.BloodPressure:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.bloodPressure.display;
    case ObservationType.BloodPressureSystolic:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.systolicBloodPressure.display;
    case ObservationType.BloodPressureDiastolic:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.diastolicBloodPressure.display;
    case ObservationType.Tobacco:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.tobacco.display;
    case ObservationType.Race:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.race.display;
    case ObservationType.Education:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.education.display;
    case ObservationType.Occupation:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.occupation.display;
    case ObservationType.Stress:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.stress.display;
    case ObservationType.DiagnosedConditions:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.diagnosedConditions.display;
    case ObservationType.ExercisePattern:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.exercisePattern.display;
    case ObservationType.FamilyConditions:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.familyConditions.display;
    case ObservationType.DietaryPattern:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.dietaryPattern.display;
    case ObservationType.PrimaryHealthConcern:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.primaryHealthConcern.display;
    case ObservationType.WellnessProfessional:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.wellnessProfessional.display;
    case ObservationType.BodyFat:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.bodyFat.display;
    case ObservationType.GripStrengthLeft:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.gripStrengthLeft.display;
    case ObservationType.GripStrengthRight:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.gripStrengthRight.display;
    case ObservationType.SuprailiacSkinfold:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.suprailiacSkinfold.display;
    // no default
  }
};

export const codeForObservationType = (type: ObservationType): string => {
  switch (type) {
    case ObservationType.Nutrition:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.food.code;
    case ObservationType.Weight:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.weight.code;
    case ObservationType.Ketones:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.ketone.code;
    case ObservationType.BreathKetones:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.breathKetone.code;
    case ObservationType.BloodGlucose:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.glucose.code;
    case ObservationType.Height:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.height.code;
    case ObservationType.WaistCircumference:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.waistCircumference.code;
    case ObservationType.ChestCircumference:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.chestCircumference.code;
    case ObservationType.NeckCircumference:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.neckCircumference.code;
    case ObservationType.ThighCircumferenceLeft:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.thighCircumferenceLeft.code;
    case ObservationType.ThighCircumferenceRight:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.thighCircumferenceRight.code;
    case ObservationType.HipCircumference:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.hipCircumference.code;
    case ObservationType.Pef:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.pef.code;
    case ObservationType.Fev:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.fev.code;
    case ObservationType.HeartRate:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.heartRate.code;
    case ObservationType.BloodPressure:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.bloodPressure.code;
    case ObservationType.BloodPressureSystolic:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.systolicBloodPressure.code;
    case ObservationType.BloodPressureDiastolic:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.diastolicBloodPressure.code;
    case ObservationType.Tobacco:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.tobacco.code;
    case ObservationType.Race:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.race.code;
    case ObservationType.Education:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.education.code;
    case ObservationType.PrimaryHealthConcern:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.primaryHealthConcern.code;
    case ObservationType.Occupation:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.occupation.code;
    case ObservationType.Stress:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.stress.code;
    case ObservationType.DiagnosedConditions:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.diagnosedConditions.code;
    case ObservationType.ExercisePattern:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.exercisePattern.code;
    case ObservationType.FamilyConditions:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.familyConditions.code;
    case ObservationType.DietaryPattern:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.dietaryPattern.code;
    case ObservationType.WellnessProfessional:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.wellnessProfessional.code;
    case ObservationType.BodyFat:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.bodyFat.code;
    case ObservationType.GripStrengthLeft:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.gripStrengthLeft.code;
    case ObservationType.GripStrengthRight:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.gripStrengthRight.code;
    case ObservationType.SuprailiacSkinfold:
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return CODE_SYSTEMS.suprailiacSkinfold.code;
    // no default
  }
};

export const codeSystemForObservationType = (
  type: ObservationType,
): fhir.Coding => {
  switch (type) {
    case ObservationType.Nutrition:
      return CODE_SYSTEMS.food;
    case ObservationType.Weight:
      return CODE_SYSTEMS.weight;
    case ObservationType.Ketones:
      return CODE_SYSTEMS.ketone;
    case ObservationType.BreathKetones:
      return CODE_SYSTEMS.breathKetone;
    case ObservationType.BloodGlucose:
      return CODE_SYSTEMS.glucose;
    case ObservationType.Height:
      return CODE_SYSTEMS.height;
    case ObservationType.WaistCircumference:
      return CODE_SYSTEMS.waistCircumference;
    case ObservationType.ChestCircumference:
      return CODE_SYSTEMS.chestCircumference;
    case ObservationType.NeckCircumference:
      return CODE_SYSTEMS.neckCircumference;
    case ObservationType.ThighCircumferenceLeft:
      return CODE_SYSTEMS.thighCircumferenceLeft;
    case ObservationType.ThighCircumferenceRight:
      return CODE_SYSTEMS.thighCircumferenceRight;
    case ObservationType.HipCircumference:
      return CODE_SYSTEMS.hipCircumference;
    case ObservationType.Pef:
      return CODE_SYSTEMS.pef;
    case ObservationType.Fev:
      return CODE_SYSTEMS.fev;
    case ObservationType.HeartRate:
      return CODE_SYSTEMS.heartRate;
    case ObservationType.BloodPressure:
      return CODE_SYSTEMS.bloodPressure;
    case ObservationType.BloodPressureSystolic:
      return CODE_SYSTEMS.systolicBloodPressure;
    case ObservationType.BloodPressureDiastolic:
      return CODE_SYSTEMS.diastolicBloodPressure;
    case ObservationType.Tobacco:
      return CODE_SYSTEMS.tobacco;
    case ObservationType.Race:
      return CODE_SYSTEMS.race;
    case ObservationType.Education:
      return CODE_SYSTEMS.education;
    case ObservationType.PrimaryHealthConcern:
      return CODE_SYSTEMS.primaryHealthConcern;
    case ObservationType.Occupation:
      return CODE_SYSTEMS.occupation;
    case ObservationType.Stress:
      return CODE_SYSTEMS.stress;
    case ObservationType.DiagnosedConditions:
      return CODE_SYSTEMS.diagnosedConditions;
    case ObservationType.ExercisePattern:
      return CODE_SYSTEMS.exercisePattern;
    case ObservationType.FamilyConditions:
      return CODE_SYSTEMS.familyConditions;
    case ObservationType.DietaryPattern:
      return CODE_SYSTEMS.dietaryPattern;
    case ObservationType.WellnessProfessional:
      return CODE_SYSTEMS.wellnessProfessional;
    case ObservationType.BodyFat:
      return CODE_SYSTEMS.bodyFat;
    case ObservationType.GripStrengthLeft:
      return CODE_SYSTEMS.gripStrengthLeft;
    case ObservationType.GripStrengthRight:
      return CODE_SYSTEMS.gripStrengthRight;
    case ObservationType.SuprailiacSkinfold:
      return CODE_SYSTEMS.suprailiacSkinfold;
    // no default
  }
};

export const observationTypeForCode = (
  codeString?: string | null,
): ObservationType | null => {
  switch (codeString) {
    case CODE_SYSTEMS.weight.code:
      return ObservationType.Weight;
    case CODE_SYSTEMS.height.code:
      return ObservationType.Height;
    case CODE_SYSTEMS.food.code:
      return ObservationType.Nutrition;
    case CODE_SYSTEMS.ketone.code:
      return ObservationType.Ketones;
    case CODE_SYSTEMS.breathKetone.code:
      return ObservationType.BreathKetones;
    case CODE_SYSTEMS.waistCircumference.code:
      return ObservationType.WaistCircumference;
    case CODE_SYSTEMS.chestCircumference.code:
      return ObservationType.ChestCircumference;
    case CODE_SYSTEMS.neckCircumference.code:
      return ObservationType.NeckCircumference;
    case CODE_SYSTEMS.thighCircumferenceLeft.code:
      return ObservationType.ThighCircumferenceLeft;
    case CODE_SYSTEMS.thighCircumferenceRight.code:
      return ObservationType.ThighCircumferenceRight;
    case CODE_SYSTEMS.hipCircumference.code:
      return ObservationType.HipCircumference;
    case CODE_SYSTEMS.glucose.code:
      return ObservationType.BloodGlucose;
    case CODE_SYSTEMS.heartRate.code:
      return ObservationType.HeartRate;
    case CODE_SYSTEMS.pef.code:
      return ObservationType.Pef;
    case CODE_SYSTEMS.fev.code:
      return ObservationType.Fev;
    case CODE_SYSTEMS.bloodPressure.code:
      return ObservationType.BloodPressure;
    case CODE_SYSTEMS.diastolicBloodPressure.code:
      return ObservationType.BloodPressureDiastolic;
    case CODE_SYSTEMS.systolicBloodPressure.code:
      return ObservationType.BloodPressureSystolic;
    case CODE_SYSTEMS.bodyFat.code:
      return ObservationType.BodyFat;
    case CODE_SYSTEMS.gripStrengthLeft.code:
      return ObservationType.GripStrengthLeft;
    case CODE_SYSTEMS.gripStrengthRight.code:
      return ObservationType.GripStrengthRight;
    case CODE_SYSTEMS.suprailiacSkinfold.code:
      return ObservationType.SuprailiacSkinfold;
    default:
      return null;
  }
};

export const defaultMrnConfig = {
  configType: MrnConfigType.typeCoding,
  system: 'http://hl7.org/fhir/v2/0203',
  code: 'MR',
};

export const SYSTEM_LIFEOMIC_DATASET = 'http://lifeomic.com/fhir/dataset';

export const SYSTEM_ACTION_PLAN =
  'http://lifeomic.com/fhir/fountain-life-care-plan-type';
export const CODE_ZORI_COACH = 'zori-coach';
export const SYSTEM_ZORI_COACH_SOURCE_RUN =
  'https://lifeomic.com/fhir/zori-coach-source-run';
export const SYSTEM_ACTION_PLAN_CATEGORY =
  'http://lifeomic.com/fhir/fountain-life-action-item-category';

export const SYSTEM_ACTION_PLAN_SYSTEM =
  'http://lifeomic.com/fhir/fountain-life-action-item-system';

export const CODE_ACTION_PLAN = 'standard';
export const SYSTEM_ACTION_PLAN_NOTES =
  'http://lifeomic.com/fhir/fountain-life-action-item-notes';

// Marker extension on legacy Zori Coach artifact activities, whose valueString is
// a JSON narrative envelope. Must match the agents-side FOUNTAIN_COACH_ARTIFACT.
// Native coach plans carry the narrative in CarePlan.description; PHC no longer
// reads the envelope or excludes these activities from the action-item list.
export const SYSTEM_ZORI_COACH_ARTIFACT =
  'https://lifeomic.com/fhir/zori-coach-artifact';

export const SYSTEM_LIFEOMIC_URL = 'http://lifeomic.com/fhir/html-url';
export const SYSTEM_ACTION_PLAN_LAST_EDITED_BY =
  'http://lifeomic.com/fhir/fountain-life-action-item-last-edited-by';

export const SYSTEM_CARE_PLAN_COMPOSITE_REPORT_DEFINITION_DURABLE_ID =
  'http://lifeomic.com/fhir/fountain-life-care-plan-composite-report-definition-durable-id';

export const SYSTEM_LAST_EDITED_BY = 'http://lifeomic.com/fhir/last-edited-by';

export const SYSTEM_RESOURCE_VISIBILITY =
  'http://lifeomic.com/fhir/resource-visibility';

// ─── Composite Report / ImpressionItem (AI Tools) FHIR system URLs ─────────
// Single source of truth for these system URLs — do not redefine them
// locally elsewhere; import from here instead.

export const SYSTEM_IMPRESSION_TYPE =
  'http://lifeomic.com/fhir/fountain-life-clinical-impression-type';

export const SYSTEM_IMPRESSION_CITATION =
  'http://lifeomic.com/fhir/fountain-life-citations';

export const SYSTEM_IMPRESSION_CITATION_ID =
  'http://lifeomic.com/fhir/fountain-life-citation-id';

export const SYSTEM_FOUNTAIN_LIFE_AI_GENERATED =
  'https://lifeomic.com/fhir/fountain-life-ai-generated';

export const SYSTEM_IMPRESSION_TEMPLATE_ID =
  'http://lifeomic.com/fhir/fountain-life-impression-template-id';

export const SYSTEM_IMPRESSION_TEMPLATE_DURABLE_ID =
  'http://lifeomic.com/fhir/fountain-life-impression-template-durable-id';

export const SYSTEM_FOUNTAIN_LIFE_AI_GENERATED_TRACE_ID =
  'http://lifeomic.com/fhir/fountain-life-ai-generated-trace-id';

/** Set by fountain_agents (FOUNTAIN_TRUNCATED) when a heuristic char-count
 * ratio indicates the LLM output hit its token limit mid-report. */
export const SYSTEM_FOUNTAIN_TRUNCATED = 'http://lifeomic.com/fhir/truncated';

export const SYSTEM_REVIEW_DIFF_DOCUMENT =
  'https://lifeomic.com/fhir/review-diff-document';

export const SYSTEM_REVIEW_PROPOSED_CITATIONS =
  'https://lifeomic.com/fhir/review-proposed-citations';

export const SYSTEM_SUGGESTION_RATIONALE =
  'https://lifeomic.com/fhir/zori-suggestion-rationale';

export const getNameFromFHIRRecord = (name: fhir3.HumanName[] | undefined) => {
  const humanName =
    findHumanName(name, {
      use: 'official',
    }) ||
    findHumanName(name, {
      findFirst: true,
    });

  if (!humanName) {
    return { given: '', family: '' };
  }

  const given = (humanName.given || []).join(' ');
  const family = humanName.family || '';

  return { given, family };
};

export const getPractitionerDisplayName = (
  practitioner: fhir3.Practitioner,
  opts: {
    defaultName: string;
    excludeQualifications?: boolean;
  },
): string => {
  const name = getNameFromFHIRRecord(practitioner?.name);

  if (!name.given || !name.family) {
    return opts.defaultName;
  }

  let displayName = '';

  if (name.family) {
    displayName += `${name.family}`;
  }

  if (name.given) {
    displayName += `${displayName ? ', ' : ''}${name.given}`;
  }

  if (opts?.excludeQualifications) {
    return displayName;
  }

  const qualifications = practitioner?.qualification
    ?.map(q => q.code.text)
    .filter(qualification => {
      return qualification && qualification?.trim?.().length > 1;
    })
    .join(' ');

  if (qualifications) {
    displayName = `${displayName} · ${qualifications}`;
  }

  return displayName;
};

export const unwrapBundle = <T extends PlatformDefaultBundle<any>>(
  bundle?: T,
): Array<T['entry'][0]['resource']> => {
  return bundle?.entry?.map(entry => entry.resource) ?? [];
};

export const isFHIRReference = (ref: unknown): ref is fhir.Reference => {
  return typeof ref === 'object' && ref !== null && 'reference' in ref;
};
